import { CryptoOptionModel, listModelFactory } from '@/models';
import ExchangeFormClient from '@/network/exchange-form';
import type { Currency, FiatCurrency, Flow, RelationLink } from '@/types';

import { updateContent } from '../../assets/js/blocks/seo-content-blocks';
import eventBus from '../../assets/js/helpers/event-bus';
import { eventNames, GlobalStorage } from '../../assets/js/home-page/global-storage';
import UserService from '@/services/UserService';
import store from '@/store/assets/index';

const popularFiatCurrenciesList = [
    'USD',
    'EUR',
    'CAD',
    'GBP',
] as Currency[];

function sortPopularFiatCurrencies(list: FiatCurrency[]) {
    const popularList = popularFiatCurrenciesList.reduce((result, popularCurrency) => {
        const currency = list.find((item => item.currency === popularCurrency));
        if (currency) {
            result.push(currency);
        }

        return result;
    }, [] as FiatCurrency[]);

    const otherList = list.filter(item => !popularFiatCurrenciesList.includes(item.currency));

    return [
        ...popularList,
        ...otherList,
    ];
}

export default {
    async fromFiatCurrencies(flow: Flow) {
        const userType= await UserService.getUserType();
        return ExchangeFormClient.fromFiatCurrencies(flow, userType)
            .then(sortPopularFiatCurrencies);
    },

    async fromCryptoCurrencies(flow: Flow) {
        const userType= await UserService.getUserType();
        return ExchangeFormClient.fromCryptoCurrencies(flow, userType).then(list => {
            return list.map(item => {
                const assetId = item!.asset;
                const asset = store.getters['assets/getAssetById'](assetId);

                item.currency = asset?.currency.code || assetId;

                return item;
            });
        }).then(listModelFactory(CryptoOptionModel));
    },

    async toFiatCurrencies(flow: Flow) {
        const userType= await UserService.getUserType();
        return ExchangeFormClient.toFiatCurrencies(flow, userType)
            .then(sortPopularFiatCurrencies);
    },

    async toCryptoCurrencies(flow: Flow) {
        const userType= await UserService.getUserType();
        return ExchangeFormClient.toCryptoCurrencies(flow, userType).then(list => {
            return list.map(item => {
                const assetId = item!.asset;
                const asset = store.getters['assets/getAssetById'](assetId);

                item.currency = asset?.currency.code || assetId;

                return item;
            });
        }).then(listModelFactory(CryptoOptionModel));
    },

    relationDetails(relationLink: RelationLink) {
        return ExchangeFormClient.relationDetails(relationLink);
    },

    swapRelations() {
        return ExchangeFormClient.swapRelations();
    },

    updatePageContent({ relationLink, currencyCodeFrom, currencyCodeTo, cryptoAssetId, isServiceFromCrypto, isServiceToCrypto }: {
        relationLink: RelationLink,
        currencyCodeFrom: string,
        currencyCodeTo: string,
        cryptoAssetId: string,
        isServiceFromCrypto: boolean,
        isServiceToCrypto: boolean
    }) {
        const oldLink = GlobalStorage.relationLink;
        const fiatCurrencyCode = isServiceFromCrypto ? currencyCodeTo : currencyCodeFrom;

        GlobalStorage.setExchangeFormData({
            relationLink,
            fiatCurrencyCode,
            cryptoCurrencyCode: isServiceFromCrypto ? currencyCodeFrom : currencyCodeTo,
            cryptoAssetId,
            isServiceFromCrypto,
            isServiceToCrypto,
        });

        if (relationLink === oldLink){
            eventBus.emit(eventNames.EVENT_RELATION_NOT_CHANGED);
            return;
        }

        return updateContent(relationLink, fiatCurrencyCode) as Promise<void>;
    },
    checkIfExistLanguage({ languages, relationLink, currentLanguage }: {
        languages: string[],
        relationLink: string,
        currentLanguage:string
    }){
        const DEFAULT_LANGUAGE = 'en';
        if (
          languages.length > 0 &&
          languages.indexOf(currentLanguage) === -1
        ) {
            window.location.href = `/${relationLink}/`;
            return false;
        }

        const languageLinkList = document.querySelectorAll('.languageLink');

        if (languageLinkList.length === 0) {
            return true;
        }
        languageLinkList.forEach((languageLink) => {
            languageLink.classList.remove('languageLink-disabled');
            const lang = languageLink.dataset.lang;
            const newLangLink =
              '/' +
              (lang !== DEFAULT_LANGUAGE ? lang + '/' : '') +
              (relationLink !== '' ? relationLink + '/' : '');

            let newEl:HTMLElement = document.createElement('a');
            newEl.href = newLangLink;
            if (languages.length > 0 && !languages.includes(lang)) {
                languageLink.classList.add('languageLink-disabled');
                newEl = document.createElement('div');
            }

            newEl.innerHTML = languageLink.innerHTML;
            languageLink.replaceWith(newEl);

            const blackList = ['href'];
            const attributes = languageLink.getAttributeNames();
            attributes.forEach((attribute) => {
                if (blackList.includes(attribute)) {
                    return;
                }

                const attributeValue = languageLink.getAttribute(attribute);

                if (attributeValue === null) {
                    return;
                }

                newEl.setAttribute(attribute, attributeValue);
            })
        });

        return true;
    },
};
